/**
 * Bepaalt of een gebruiker een route mag openen, op basis van zijn menu.
 *
 * Alleen deze prefixen worden gevalideerd; de rest van de applicatie is vrij toegankelijk.
 *
 * `/skills/` staat hier bewust niet meer bij: dat pad is vervangen door `/admin/skills/` en is nu
 * alleen nog een redirect (zie aca-main-page.js). Zou het hier staan, dan blokkeert deze guard de
 * route voordat de redirect kan lopen — het doel `/admin/skills/...` wordt alsnog gevalideerd.
 *
 * Het menu-item Afdelingen heeft `/department` als href, en daar staat nu ook de lijst. Zowel die
 * lijst als een afdelingsdetail (`/department/<id>/employees`) valt daaronder en wordt dus
 * gevalideerd: zonder dat menu-item komt een gebruiker ook niet via een deep link binnen.
 */
const PATHS_TO_VALIDATE = ['/admin', '/department'];

/**
 * Valt de route binnen dit pad? Alleen op een padgrens, dus `/department` dekt
 * `/department/1234` maar niet `/departmentmandatoryusers/1234` — dat is een ander scherm.
 */
function isUnder(path, route) {
    return route === path || route.startsWith(path + '/');
}

/** Alle hrefs in de menuboom, op elk niveau. */
function collectHrefs(items, acc = []) {
    if (!Array.isArray(items)) return acc;

    for (const item of items) {
        if (item?.href) acc.push(item.href);
        collectHrefs(item?.subMenuItems, acc);
    }

    return acc;
}

/**
 * Een route is toegestaan als hij gelijk is aan een menu-href, eronder valt (deep link),
 * of er de bovenliggende map van is (`/admin/skills` bij een menu-item `/admin/skills/actions`).
 */
function matches(href, route) {
    return isUnder(href, route) || href.startsWith(route + '/');
}

function isRestricted(route) {
    return PATHS_TO_VALIDATE.some((path) => isUnder(path, route));
}

function isMenuLoaded(menu) {
    return Array.isArray(menu?.menuItems);
}

export function isRouteAllowed(menu, route) {
    if (!isRestricted(route)) return true;

    // Menu is nog niet geladen: nog niets toestaan.
    if (!isMenuLoaded(menu)) return false;

    return collectHrefs(menu.menuItems).some((href) => matches(href, route));
}

/**
 * Wacht het oordeel over deze route nog op het menu? Zonder menu zegt isRouteAllowed nee
 * tegen elke bewaakte route, en dat is de veilige kant. Maar voor de weergave is het geen
 * weigering: het menu is er alleen nog niet. Sinds de login client-side navigeert (#6566)
 * komt dat bij een deep link naar beheer even voor, en dan hoort daar nog niets te staan
 * in plaats van "niet bevoegd".
 */
export function isAwaitingMenu(menu, route) {
    return isRestricted(route) && !isMenuLoaded(menu);
}

export default isRouteAllowed;
